/* The chat page's tasks (spec 2026-09-17-agent-tasks §4.9): a rail section
 * and a side panel. Admin only -- the parent renders neither otherwise.
 * Imported by chat.jsx, so it is bundled into chat.js, not a build entry. */
import { TASK_POLL_MS, taskChip, taskActions, nextFireText, runCost, mandateLabel, mandateActions, mandateExtras, mailLine } from './chat-tasks-logic.mjs';
import { revealControls } from './workbench-logic.mjs';
import { chatCls } from './chat-render.jsx';
import { confirmAction } from './feedback.jsx';

const { useState, useEffect, useCallback } = React;

function errText(e) {
  return String((e && (e.detail || e.message)) || e);
}

// Agent- and admin-authored text shows its invisible controls as chips, as on the
// workbench (RB71): a U+202E in a summary must not reorder what the admin reads (review M8).
function Revealed({ text }) {
  return revealControls(text).segments.map((s, i) => (s.type === 'control'
    ? <span key={i} className="wb-ctl">{s.label}</span>
    : <React.Fragment key={i}>{s.text}</React.Fragment>));
}

export function TasksRail({ tasks, error, activeTaskId, canCreate, lang, onSelect }) {
  const he = lang === 'he';
  return (
    <>
      <div className="nav-section chat-rail-label">{he ? 'משימות' : 'Tasks'}</div>
      {error && <div className="chat-rail-empty" dir="auto">{error}</div>}
      {!error && tasks.length === 0 && (
        <div className="chat-rail-empty">
          {canCreate
            ? (he ? 'אין משימות — בקשו מהסוכן לתזמן אחת' : 'No tasks yet — ask the agent to schedule one')
            : (he ? 'רק מנהל עם שם משתמש יכול לתת לסוכן משימות — התחברו כמנהל בשם' : 'Only a named admin can give the agent tasks — log in with your admin username')}
        </div>
      )}
      {tasks.map((t) => (
        <div key={t.id} className={chatCls('chat-conv', t.id === activeTaskId && 'active')}
             role="button" tabIndex={0} onClick={() => onSelect(t.id)}
             onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(t.id); } }}>
          <span className="chat-conv-title" dir="auto"><Revealed text={t.title}/></span>
          <span className="chat-conv-time mono">{nextFireText(t, lang)}</span>
        </div>
      ))}
    </>
  );
}

// tenantId rides alongside taskId: GET/PATCH /api/v1/agent-tasks/{id} require it as a
// query param (app/routers/agent_tasks.py's own convention over a per-tenant resource),
// so it must come from the caller -- the task row `activeTask` already carries it.
export function TaskPanel({ taskId, tenantId, lang, onChanged, onOpenRun }) {
  const he = lang === 'he';
  const [d, setD] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => PCMAdapter.getAgentTask(taskId, tenantId)
    .then((x) => { setD(x); setError(''); })
    .catch((e) => setError(errText(e))), [taskId, tenantId]);

  useEffect(() => {
    setD(null);
    load();
    const h = setInterval(load, TASK_POLL_MS);
    return () => clearInterval(h);
  }, [load]);

  const act = async (action) => {
    if (action === 'cancel' && !await confirmAction(he ? 'לבטל את המשימה? ריצות ממתינות יבוטלו.' : 'Cancel this task? Its queued and sleeping runs are cancelled too.', { lang, danger: true, confirmLabel: he ? 'ביטול המשימה' : 'Cancel task' })) return;
    setBusy(true);
    PCMAdapter.patchAgentTask(taskId, tenantId, { action })
      .then(() => { setBusy(false); load(); onChanged(); })
      .catch((e) => { setBusy(false); setError(errText(e)); });
  };

  const decideMandate = async (approved) => {
    // Capture exactly what this render showed; a newer proposal must be refused by the server.
    // The whole stored mandate, so a v2's `writes` and `expires_at` ride in it as shown: the
    // server compares it with the stored proposal key for key (mandate.decide_mandate).
    const expected = d.mandate;
    const ask = mandateExtras(expected).any
      ? (he ? 'לאשר את ההרשאה המוצגת: נמענים, מטרה, ממשקים ותוקף?' : 'Approve the displayed mandate: its recipients, purpose, interfaces and expiry?')
      : (he ? 'לאשר את הנמענים והמטרה המוצגים?' : 'Approve the displayed recipients and purpose?');
    if (approved && !await confirmAction(ask, { lang, confirmLabel: he ? 'אישור' : 'Approve' })) return;
    setBusy(true); setError('');
    try {
      await PCMAdapter.decideTaskMandate(taskId, tenantId, approved, expected);
      await load(); onChanged();
    } catch (e) { setError(errText(e)); }
    finally { setBusy(false); }
  };

  if (!d) return error ? <div role="alert" className="set-error" dir="auto">{error}</div> : <div className="dim">{he ? 'טוען…' : 'Loading…'}</div>;
  const label = { pause: he ? 'השהיה' : 'Pause', resume: he ? 'המשך' : 'Resume', cancel: he ? 'ביטול' : 'Cancel' };
  const extras = mandateExtras(d.mandate);
  return (
    <>
      {/* dir="ltr": three ASCII fields joined by "·" are separate bidi runs, and an RTL
          paragraph reorders multiple LTR runs relative to each other even though each
          one's own characters stay left-to-right (measured in the browser pass). */}
      <div><span className={`chip ${taskChip(d)}`}>{d.status}</span> <span className="mono dim" dir="ltr">#{d.id} · {d.env} · {d.model}</span></div>
      {d.status_reason && <div className="dim" dir="auto">{d.status_reason}</div>}
      <div className="wb-card-sub">{he ? 'הרשאת דואר' : 'Mail mandate'}</div>
      <div>{mandateLabel({ ...d.mandate, status:d.mandate_status })}</div>
      {d.mandate && <>
        <div>{he ? 'נמענים' : 'Recipients'}: {d.mandate.recipients.map((a, i) => <React.Fragment key={a}>{i > 0 ? ', ' : ''}<bdi dir="auto"><Revealed text={a}/></bdi></React.Fragment>)}</div>
        {/* Mandate v2 (seats PR 3): the admin approves what it reads, so each key the decision
            carries is on screen -- only when present, as the mail and Telegram asks word it. */}
        {extras.domains && <div className="dim">{he ? 'נמען שמתחיל ב-@ הוא כל כתובת בדומיין הזה בדיוק (לא בתת-דומיין שלו).' : 'A recipient written @domain is anyone at exactly that domain (not its subdomains).'}</div>}
        <div>{he ? 'מטרה' : 'Purpose'}: <bdi dir="auto"><Revealed text={d.mandate.purpose}/></bdi></div>
        {extras.writes.length > 0 && <div>{he ? 'ממשקים שמותר לה לטעון בלי לשאול' : 'Interfaces it may load without asking'}: {extras.writes.map((w, i) => <React.Fragment key={w}>{i > 0 ? ', ' : ''}<bdi dir="ltr"><Revealed text={w}/></bdi></React.Fragment>)}</div>}
        {extras.expiresAt && <div>{he ? 'מסתיימת אחרי' : 'Ends after'}: <bdi dir="ltr">{extras.expiresAt}</bdi> {he ? '(אזור הזמן של החברה)' : "(the company's time zone)"}</div>}
      </>}
      {d.status !== 'cancelled' && <div className="wb-actions">{mandateActions(d.mandate_status).map(action =>
        <button key={action} type="button" className="btn btn-sm" disabled={busy}
          onClick={() => decideMandate(action === 'approve')}>{action === 'approve' ? (he ? 'אישור' : 'Approve') : (he ? 'דחייה' : 'Reject')}</button>)}</div>}
      <div className="wb-card-sub">{he ? 'דואר (10 האחרונים)' : 'Mail (last 10)'}</div>
      {!(d.mail || []).length && <div className="dim">{he ? 'אין דואר עדיין' : 'No mail yet'}</div>}
      {(d.mail || []).map(m => <details key={m.id} className="wb-prompt">
        <summary aria-label={[mailLine(m), m.subject, m.error].filter(Boolean).join(' — ')}>{m.direction === 'out' ? 'To: ' : 'From: '}{(m.direction === 'out' ? m.to : [m.from || '—']).map((a, i) => <React.Fragment key={i}>{i ? ', ' : ''}<bdi dir="auto"><Revealed text={a}/></bdi></React.Fragment>)} · <bdi>{m.status}</bdi> · <bdi dir="auto"><Revealed text={m.subject}/></bdi>{m.error && <span className="set-error" dir="auto" style={{unicodeBidi:'plaintext'}}><Revealed text={m.error}/></span>}</summary>
        <div>Cc: {m.cc.map(a => <bdi key={a} dir="auto"><Revealed text={a}/>{' '}</bdi>)}</div>
        <pre className="wb-body" dir="auto" style={{unicodeBidi:'plaintext'}}><Revealed text={m.body}/></pre>
      </details>)}
      <div className="wb-card-sub">{he ? 'תזמון' : 'Schedule'}</div>
      <div>{d.schedule_text} — {nextFireText(d, lang)}</div>
      <div className="wb-card-sub">{he ? 'הוראות' : 'Instructions'}</div>
      <pre className="wb-body" dir="auto"><Revealed text={d.instructions}/></pre>
      <div className="wb-card-sub">{he ? 'הערות מהריצה האחרונה' : 'Notes from the last run'}</div>
      <pre className="wb-body" dir="auto">{d.notes ? <Revealed text={d.notes}/> : (he ? '(אין עדיין)' : '(none yet)')}</pre>
      {d.pending_approvals.length > 0 && (
        <div className="dim">
          {/* dir="auto" per element, not on this wrapper: agent-authored, Hebrew-capable
              text (same field workbench.jsx renders with element-level dir="auto") -- a
              container dir resolves from whichever title it meets first and misaligns the
              rest (review F1, memory dir-auto-fails-on-containers). */}
          {d.pending_approvals.map((a) => <div key={a.id} dir="auto"><Revealed text={a.title}/></div>)}
        </div>
      )}
      <div className="wb-card-sub">{he ? `ריצות (תקציב לריצה ${runCost(d.run_budget_usd)})` : `Runs (budget per run ${runCost(d.run_budget_usd)})`}</div>
      <div className="tbl-wrap" tabIndex={0}>
        <table className="tbl">
          <tbody>
            {d.runs.length === 0 && <tr><td className="dim">{he ? 'עוד לא רצה' : 'Not run yet'}</td></tr>}
            {d.runs.map((r) => (
              <tr key={r.id}>
                <td className="mono">
                  <button type="button" className="btn btn-sm" onClick={() => onOpenRun(d.tenant_id, r.id)}>#{r.id}</button>
                </td>
                <td>{r.status}</td>
                <td className="mono">{runCost(r.cost_usd)}</td>
                <td dir="auto"><Revealed text={r.error || r.summary || ''}/></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {error && <div role="alert" className="set-error" dir="auto">{error}</div>}
      <div className="wb-actions">
        {taskActions(d).map((a) => {
          // Never a string literal inside className={...}: check_css_classes.py reads every quoted
          // string there as a class name, and `a === 'cancel'` made it report ".cancel" (review C2).
          const danger = a === 'cancel';
          return (
            <button type="button" key={a} className={chatCls('btn', 'btn-sm', danger && 'danger')}
                    disabled={busy} onClick={() => act(a)}>{label[a]}</button>
          );
        })}
      </div>
    </>
  );
}
